import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  StepDefinitionSchema,
  EdgeDefinitionSchema,
  WorkflowRunStatusSchema,
  StepRunStatusSchema,
  StepCategorySchema,
  StepLibraryModuleSchema,
  SocketDefinitionSchema,
} from "@erp/workflows";
import { SystemEntity } from "@erp/shared-types";
import {
  sandboxInstanceSchema,
  sandboxSpanSchema,
  sandboxLogLineSchema,
} from "./sandboxes.contract";
import { ToolCallRunResponseSchema } from "./agent-definitions.contract";

/**
 * Orchestrator type for workflow execution
 */
export const OrchestratorTypeSchema = z.literal("temporal");

/**
 * Workflow definition creation request
 */
export const CreateWorkflowDefinitionRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  steps: z.array(StepDefinitionSchema),
  edges: z.array(EdgeDefinitionSchema),
  tags: z.array(z.string()).optional(),
  orchestratorType: OrchestratorTypeSchema.optional(),
  /** When true (default), validates the graph before persisting. Draft saves skip validation. */
  validateGraph: z.boolean().optional(),
});

/**
 * Saved test case for manual workflow testing
 */
export const WorkflowTestCaseSchema = z.object({
  id: z.uuid(),
  workflowDefinitionId: z.uuid(),
  createdBy: z.uuid(),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  eventData: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type WorkflowTestCase = z.infer<typeof WorkflowTestCaseSchema>;

/**
 * Test case CRUD schemas
 */
export const CreateTestCaseRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  eventData: z.record(z.string(), z.unknown()),
});

export const UpdateTestCaseRequestSchema = z.object({
  testCaseId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  eventData: z.record(z.string(), z.unknown()).optional(),
});

export const DeleteTestCaseRequestSchema = z.object({
  testCaseId: z.uuid(),
});

export const ListTestCasesRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
});

export const RunTestCaseRequestSchema = z.object({
  testCaseId: z.uuid(),
});

export const CheckTestCaseCompatibilityRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  steps: z.array(StepDefinitionSchema),
});

export const CheckTestCaseCompatibilityResponseSchema = z.object({
  compatible: z.array(WorkflowTestCaseSchema),
  incompatible: z.array(WorkflowTestCaseSchema),
});

/**
 * Workflow definition update request — version-content only.
 *
 * Lineage-level fields (name, description, tags, isActive, orchestratorType)
 * moved to updateWorkflow. This endpoint now only accepts draft-editable
 * content: steps and edges. Targets a specific WorkflowDefinition row.
 */
export const UpdateWorkflowDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  steps: z.array(StepDefinitionSchema).optional(),
  edges: z.array(EdgeDefinitionSchema).optional(),
  /** When true, validates the graph before persisting (e.g. publish). Draft saves skip validation. */
  validateGraph: z.boolean().optional(),
});

/**
 * Workflow update request — workflow-level (lineage-level) fields.
 *
 * Applied immediately to the Workflow row, affecting every version's
 * display + trigger behavior. Independent of which version is viewed.
 */
export const UpdateWorkflowRequestSchema = z.object({
  workflowId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  isActive: z.boolean().optional(),
  isComponent: z.boolean().optional(),
  exposedInputs: z.array(z.string()).nullable().optional(),
  exposedOutputs: z.array(z.string()).nullable().optional(),
  orchestratorType: OrchestratorTypeSchema.optional(),
  fanoutStrategy: z.enum(["parallel", "sequential"]).optional(),
  retryConfig: z.record(z.string(), z.unknown()).nullable().optional(),
  workflowManagerAgentDefinitionId: z.uuid().nullable().optional(),
});

/**
 * Workflow definition duplicate request
 */
export const DuplicateWorkflowDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.string().uuid(),
  name: z.string().min(1).max(255),
});

/**
 * Versioning lifecycle status. See docs/projectplans/workflow-versioning.md.
 */
export const WorkflowDefinitionStatusSchema = z.enum(["draft", "finalized"]);

/**
 * Workflow (lineage-level entity) response.
 *
 * Workflow-level fields — name, description, tags, isActive, orchestratorType,
 * etc. — live here. One Workflow owns many WorkflowDefinition rows (versions).
 * `pinnedVersionId` identifies the canonical version used by triggers.
 */
export const WorkflowResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  tags: z.array(z.string()),
  isActive: z.boolean(),
  isComponent: z.boolean(),
  exposedInputs: z.array(z.string()).nullable().optional(),
  exposedOutputs: z.array(z.string()).nullable().optional(),
  orchestratorType: OrchestratorTypeSchema,
  fanoutStrategy: z.enum(["parallel", "sequential"]),
  retryConfig: z.record(z.string(), z.unknown()).nullable().optional(),
  workflowManagerAgentDefinitionId: z.uuid().nullable().optional(),
  pinnedVersionId: z.uuid().nullable().optional(),
  createdBy: z.uuid(),
  createdByName: z.string().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * Workflow definition response (a single immutable-once-finalized version).
 *
 * Lineage-level fields (name/description/tags/isActive/orchestratorType) live
 * on the Workflow, not here — fetch them via `getWorkflow(workflowId)` when
 * you need them. `steps` and `edges` are the graph snapshot this version
 * executes.
 */
export const WorkflowDefinitionResponseSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  parentVersionId: z.uuid().nullable().optional(),
  version: z.number(),
  status: WorkflowDefinitionStatusSchema,
  // Derived at mapping time: `true` when this version's id matches the
  // owning Workflow's pinnedVersionId.
  isPinned: z.boolean(),
  changelog: z.string().nullable().optional(),
  createdBy: z.uuid(),
  steps: z.array(StepDefinitionSchema),
  edges: z.array(EdgeDefinitionSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * Trigger workflow request
 */
export const TriggerWorkflowRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  initialInputs: z.record(z.string(), z.unknown()).optional(),
  rerunOfWorkflowRunId: z.uuid().optional(),
});

/**
 * Workflow run response
 */
export const WorkflowRunResponseSchema = z.object({
  id: z.uuid(),
  workflowDefinitionId: z.uuid(),
  // Workflow (lineage) id — denormalized on the run row for fast grouping.
  workflowId: z.uuid(),
  // Snapshot of the version this run executed against (joined at read time).
  // Version number is stable for any given run since definitions are immutable.
  workflowDefinitionVersion: z.number().optional(),
  workflowDefinitionIsPinned: z.boolean().optional(),
  workflowDefinitionChangelog: z.string().nullable().optional(),
  status: WorkflowRunStatusSchema,
  triggeredBy: z.uuid().nullable(), // null for system-triggered workflows
  triggeredByName: z.string().optional(), // User's display name if triggered by a user
  triggeredAt: z.iso.datetime(),
  startedAt: z.iso.datetime().optional(),
  completedAt: z.iso.datetime().optional(),
  stopReason: z.string().optional(),
  errorStepId: z.uuid().optional(),
  totalSteps: z.number(),
  completedSteps: z.number(),
  wallTimeMs: z.number().optional(),
  userTimeMs: z.number().optional(),
  systemTimeMs: z.number().optional(),
  sentryTraceId: z.string().optional(),
  // Parent-child workflow support
  parentWorkflowRunId: z.uuid().optional(),
  parentContext: z.record(z.string(), z.unknown()).optional(),
  rerunOfWorkflowRunId: z.uuid().optional(),
  hasBeenRerun: z.boolean().optional(),
  testCaseId: z.uuid().optional(),
});

/**
 * Get workflow run request
 */
export const GetWorkflowRunRequestSchema = z.object({
  workflowRunId: z.uuid(),
});

/**
 * Step run response
 */
export const StepRunResponseSchema = z.object({
  id: z.uuid(),
  workflowRunId: z.uuid(),
  stepDefinitionId: z.uuid(),
  attemptNumber: z.number(),
  status: StepRunStatusSchema,
  inputData: z.record(z.string(), z.unknown()),
  outputData: z.record(z.string(), z.unknown()).optional(),
  queuedAt: z.iso.datetime().optional(),
  startedAt: z.iso.datetime().optional(),
  completedAt: z.iso.datetime().optional(),
  duration: z.number().optional(),
  stopReason: z.string().optional(),
  sentrySpanId: z.string().optional(),
  /** Linked agent run ID for background_agent steps */
  agentRunId: z.uuid().optional(),
  /** Guardrail evaluation snapshots (present when step is in review or was blocked) */
  guardrailEvaluations: z
    .array(
      z.object({
        name: z.string(),
        kind: z.string(),
        stage: z.string(),
        action: z.string(),
        decision: z.string(),
        triggered: z.boolean(),
        reason: z.string(),
        source: z.string(),
        path: z.string().optional(),
        payloadPreview: z.string().optional(),
        metadata: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .optional(),
});

export {
  WorkflowExecutionDurationPattern,
  WorkflowExecutionDurationSchema,
  WorkflowStepRetryOverrideSchema,
  WorkflowStepExecutionOverrideSchema,
  WorkflowStepConfigExecutionOverrideSchema,
  type WorkflowStepExecutionOverride,
} from "@erp/workflows";

/**
 * List available step types response
 */
export const ListStepTypesResponseSchema = z.object({
  stepTypes: z.array(
    z.object({
      type: z.string(),
      name: z.string(),
      description: z.string(),
      category: StepCategorySchema,
      libraryModule: StepLibraryModuleSchema,
      inputSockets: z.array(SocketDefinitionSchema),
      outputSockets: z.array(SocketDefinitionSchema),
      // JSON Schema representation of the step's config schema
      configSchema: z.record(z.string(), z.unknown()).optional(),
      evalConfig: z
        .object({
          evalOutputKeys: z.array(z.string()),
          outputFormat: z.enum(["parallel_arrays", "object"]).optional(),
          contextOutputKeys: z.array(z.string()).optional(),
        })
        .optional(),
      deprecated: z
        .object({
          reason: z.string(),
          replacedBy: z.string().optional(),
        })
        .optional(),
      visibility: z
        .object({
          visibility: z.enum(["visible", "hidden", "restricted", "disabled"]),
          predicateResults: z
            .array(
              z.array(
                z.object({
                  type: z.string(),
                  passed: z.boolean(),
                  detail: z.string(),
                }),
              ),
            )
            .optional(),
          disabledReason: z.string().optional(),
        })
        .optional(),
    }),
  ),
});

export const ActionWorkflowInterfaceSchema = z.object({
  inputs: z.array(SocketDefinitionSchema),
  outputs: z.array(SocketDefinitionSchema),
});

export const ActionWorkflowOptionSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  name: z.string(),
  description: z.string().optional(),
  orchestratorType: OrchestratorTypeSchema,
  interface: ActionWorkflowInterfaceSchema,
});

export const ListActionWorkflowOptionsResponseSchema = z.object({
  workflows: z.array(ActionWorkflowOptionSchema),
});

/**
 * Socket definition for templates (uses permissive string type)
 */
export const TemplateSocketDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(), // More permissive than SocketDefinitionSchema
  required: z.boolean(),
  description: z.string().optional(),
});

/**
 * Workflow template step schema (simplified from StepDefinitionSchema)
 */
export const TemplateStepSchema = z.object({
  stepType: z.string(),
  name: z.string(),
  category: z.string(),
  config: z.record(z.string(), z.unknown()),
  position: z.object({ x: z.number(), y: z.number() }),
  sockets: z.object({
    inputs: z.array(TemplateSocketDefinitionSchema),
    outputs: z.array(TemplateSocketDefinitionSchema),
  }),
  componentWorkflowId: z.uuid().optional(),
  emits: z.array(z.string()).optional(),
});

/**
 * Workflow template edge schema
 */
export const TemplateEdgeSchema = z.object({
  sourceStepIndex: z.number(),
  sourceSocketId: z.string(),
  targetStepIndex: z.number(),
  targetSocketId: z.string(),
});

/**
 * Workflow template response - full template structure
 */
export const WorkflowTemplateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  version: z.number(),
  isActive: z.boolean(),
  tags: z.array(z.string()),
  orchestratorType: OrchestratorTypeSchema.optional(),
  isComponent: z.boolean(),
  steps: z.array(TemplateStepSchema),
  edges: z.array(TemplateEdgeSchema),
  retryConfig: z
    .object({
      enabled: z.boolean(),
      maxRetries: z.number(),
      initialDelay: z.number(),
      maxDelay: z.number(),
      backoffMultiplier: z.number(),
    })
    .optional(),
  exposedInputs: z.array(z.string()).optional(),
  exposedOutputs: z.array(z.string()).optional(),
  category: z
    .enum(["email", "ap", "po", "integration", "rebates", "custom"])
    .optional(),
});

/**
 * List workflow templates response
 */
export const ListWorkflowTemplatesResponseSchema = z.object({
  templates: z.array(WorkflowTemplateResponseSchema),
});

/**
 * Instantiate workflow template request
 */
export const InstantiateWorkflowTemplateRequestSchema = z.object({
  templateId: z.string(),
});

/**
 * Schedule info for cron-triggered workflows
 */
export const WorkflowScheduleInfoSchema = z.object({
  cronExpression: z.string(),
  timezone: z.string(),
  description: z.string().optional(),
});

/**
 * Event trigger info for event-driven workflows
 */
export const WorkflowEventTriggerInfoSchema = z.object({
  eventType: z.string(),
});

export const WorkflowGroupFrameSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const WorkflowGroupSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()),
  workflowCount: z.number(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const WorkflowGroupImplicitLinkSchema = z.object({
  sourceWorkflowId: z.uuid(),
  sourceStepId: z.uuid(),
  eventType: z.string(),
  targetWorkflowId: z.uuid(),
  targetStepId: z.uuid(),
});

/**
 * Group member shape — the version graph (steps/edges) plus the lineage-level
 * fields that the group UI needs for frame labels (name, tags, isActive,
 * orchestratorType). We merge the two shapes here instead of nesting a
 * `workflow: WorkflowResponse` object so the frontend rendering code stays
 * flat — and so the OpenAPI surface keeps the fields at the top level where
 * external consumers expect them.
 */
export const WorkflowGroupMemberSchema =
  WorkflowDefinitionResponseSchema.extend({
    pinnedDefinitionId: z.uuid(),
    name: z.string(),
    description: z.string().nullable().optional(),
    tags: z.array(z.string()),
    isActive: z.boolean(),
    isComponent: z.boolean(),
    orchestratorType: OrchestratorTypeSchema,
    organizationId: z.uuid(),
  });

export const WorkflowGroupResponseSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()),
  workflowFrames: z.record(z.string(), WorkflowGroupFrameSchema),
  workflows: z.array(WorkflowGroupMemberSchema),
  implicitLinks: z.array(WorkflowGroupImplicitLinkSchema),
  missingWorkflowIds: z.array(z.uuid()),
  createdBy: z.uuid(),
  organizationId: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateWorkflowGroupRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()),
  workflowFrames: z.record(z.string(), WorkflowGroupFrameSchema).optional(),
});

export const UpdateWorkflowGroupRequestSchema = z.object({
  workflowGroupId: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  workflowIds: z.array(z.uuid()).optional(),
  workflowFrames: z.record(z.string(), WorkflowGroupFrameSchema).optional(),
});

/**
 * Workflow summary — one row per Workflow for listing pages.
 *
 * The `id` is the Workflow row id (the lineage). `pinnedVersionId`
 * points at the canonical version used by triggers; `pinnedVersionNumber`
 * and `pinnedVersionChangelog` give the UI enough to show "v7 · Active"
 * without a second fetch.
 */
export const WorkflowDefinitionSummarySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  // Matches `WorkflowResponseSchema.description` (nullable in the DB) —
  // otherwise a single workflow with `description: null` fails the whole
  // list-response parse and the workflows page 500s.
  description: z.string().nullable().optional(),
  tags: z.array(z.string()),
  isActive: z.boolean(),
  orchestratorType: OrchestratorTypeSchema,
  createdBy: z.uuid(),
  createdByName: z.string().nullable().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().optional(),
  nodeCount: z.number(),
  lastRun: WorkflowRunResponseSchema.optional(),
  scheduleInfo: WorkflowScheduleInfoSchema.optional(),
  eventTriggerInfo: WorkflowEventTriggerInfoSchema.optional(),
  // Pinned-version snapshot embedded for list display.
  pinnedVersionId: z.uuid().nullable().optional(),
  pinnedVersionNumber: z.number().optional(),
  pinnedVersionChangelog: z.string().nullable().optional(),
  draftCount: z.number().optional(),
  versionCount: z.number().optional(),
});

/**
 * List workflow definition summaries response
 */
export const ListWorkflowDefinitionSummariesResponseSchema = z.object({
  workflows: z.array(WorkflowDefinitionSummarySchema),
  total: z.number(),
  /** All distinct tags across the org's workflows (for the tag filter UI). */
  availableTags: z.array(z.string()),
});

/**
 * List runs for a workflow definition request
 */
export const ListWorkflowRunsForDefinitionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  limit: z.number().int().min(1).max(250).optional(),
  offset: z.number().int().min(0).optional(),
  status: WorkflowRunStatusSchema.optional(),
  triggeredByType: z.enum(["user", "system"]).optional(),
  triggeredFrom: z.iso.datetime().optional(),
  triggeredTo: z.iso.datetime().optional(),
  search: z.string().min(1).max(255).optional(),
  testCaseId: z.uuid().optional(),
});

/**
 * List runs for a workflow definition response
 */
export const ListWorkflowRunsForDefinitionResponseSchema = z.object({
  runs: z.array(WorkflowRunResponseSchema),
  total: z.number(),
});

/**
 * List rerun children of a workflow run
 */
export const ListRerunsForRunRequestSchema = z.object({
  workflowRunId: z.uuid(),
});

export const RerunSummarySchema = z.object({
  id: z.uuid(),
  status: WorkflowRunStatusSchema,
  triggeredAt: z.iso.datetime(),
});

export const ListRerunsForRunResponseSchema = z.object({
  reruns: z.array(RerunSummarySchema),
});

/**
 * Retry step request - retry a failed/timed_out step with optional input override
 */
export const RetryStepRequestSchema = z.object({
  workflowRunId: z.uuid(),
  stepRunId: z.uuid(),
  inputData: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Retry step response
 */
export const RetryStepResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
});

/**
 * Restart from step request - restart workflow from a specific step
 */
export const RestartFromStepRequestSchema = z.object({
  workflowRunId: z.uuid(),
  workflowStepId: z.uuid(),
  inputData: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Restart from step response
 */
export const RestartFromStepResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
  cancelledStepRunIds: z.array(z.uuid()),
});

/**
 * Resolve guardrail review request - confirm or dismiss a guardrail violation
 */
export const ResolveGuardrailReviewRequestSchema = z.object({
  workflowRunId: z.uuid(),
  stepRunId: z.uuid(),
  decision: z.enum(["confirmed", "dismissed"]),
  comment: z.string().optional(),
});

/**
 * Resolve guardrail review response
 */
export const ResolveGuardrailReviewResponseSchema = z.object({
  stepRun: StepRunResponseSchema,
  workflowRun: WorkflowRunResponseSchema,
});

/**
 * Get entity workflow context request
 */
export const GetEntityWorkflowContextRequestSchema = z.object({
  entityType: z.nativeEnum(SystemEntity),
  entityId: z.uuid(),
});

/**
 * Executed workflow step context for entity rerun dropdown
 */
export const EntityWorkflowExecutedStepSchema = z.object({
  workflowStepId: z.uuid(),
  stepName: z.string(),
  stepType: z.string(),
  latestStepRun: StepRunResponseSchema.nullable(),
});

/**
 * Minimal parent-run reference used to deep-link from an entity's workflow
 * context to the parent workflow run that spawned the current one. We only
 * carry the two ids the run-detail route needs (workflowId for the layout,
 * id for the run itself) — anything richer should go through getRun.
 */
export const EntityWorkflowParentRunSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
});

/**
 * Get entity workflow context response
 */
export const GetEntityWorkflowContextResponseSchema = z.object({
  context: z
    .object({
      workflowRun: WorkflowRunResponseSchema,
      workflowDefinitionName: z.string(),
      steps: z.array(EntityWorkflowExecutedStepSchema),
      edges: z.array(EdgeDefinitionSchema),
      parentWorkflowRun: EntityWorkflowParentRunSchema.nullable(),
    })
    .nullable(),
});

// ---------------------------------------------------------------------------
// Versioning endpoints
// See docs/projectplans/workflow-versioning.md for the full design.
// ---------------------------------------------------------------------------

/**
 * Summary of a single version in a lineage — used by the version-history UI.
 * Includes author info so the UI can render "v3 by Alice on 2026-04-20".
 */
export const WorkflowVersionSummarySchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  parentVersionId: z.uuid().nullable().optional(),
  version: z.number(),
  status: WorkflowDefinitionStatusSchema,
  // Derived at mapping time from `workflow.pinnedVersionId === id`.
  isPinned: z.boolean(),
  changelog: z.string().nullable().optional(),
  createdBy: z.uuid(),
  createdByName: z.string().optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateDraftRequestSchema = z.object({
  workflowId: z.uuid(),
  /**
   * Version to fork the draft from. If omitted, defaults to the workflow's
   * currently-pinned version. Passing a historical version creates a draft
   * whose parent is that historical version — useful for "I want to branch
   * off an old version to bring back its behavior".
   */
  sourceVersionId: z.uuid().optional(),
});

export const CreateDraftResponseSchema = z.object({
  definition: WorkflowDefinitionResponseSchema,
  /**
   * If true, the user already had a draft in this lineage and we returned it
   * instead of creating a new one. Prevents multiple drafts per user per lineage.
   */
  resumed: z.boolean(),
});

export const FinalizeVersionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  /**
   * If the draft's parent version is no longer the pinned version, finalize
   * returns a stale-base warning instead of performing the action. Re-post
   * with `acknowledgeStaleBase: true` to proceed anyway.
   */
  acknowledgeStaleBase: z.boolean().optional(),
});

export const StaleBaseWarningSchema = z.object({
  kind: z.literal("stale_base"),
  draftParentVersionId: z.uuid().nullable(),
  draftParentVersionNumber: z.number().nullable(),
  currentPinnedVersionId: z.uuid().nullable(),
  currentPinnedVersionNumber: z.number().nullable(),
  message: z.string(),
});

export const FinalizeVersionResponseSchema = z.discriminatedUnion("ok", [
  z.object({
    ok: z.literal(true),
    definition: WorkflowDefinitionResponseSchema,
  }),
  z.object({ ok: z.literal(false), warning: StaleBaseWarningSchema }),
]);

export const PinVersionRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  acknowledgeStaleBase: z.boolean().optional(),
});

export const PinVersionResponseSchema = z.discriminatedUnion("ok", [
  z.object({
    ok: z.literal(true),
    definition: WorkflowDefinitionResponseSchema,
  }),
  z.object({ ok: z.literal(false), warning: StaleBaseWarningSchema }),
]);

export const UpdateVersionChangelogRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
  changelog: z.string().max(5000),
});

export const ListVersionsRequestSchema = z.object({
  workflowId: z.uuid(),
});

export const ListVersionsResponseSchema = z.object({
  versions: z.array(WorkflowVersionSummarySchema),
});

export const ListDraftsRequestSchema = z.object({
  /**
   * If true (default), list drafts owned by the calling user. If false, list
   * all drafts in the organization.
   */
  ownedByCurrentUser: z.boolean().optional(),
});

export const ListDraftsResponseSchema = z.object({
  drafts: z.array(WorkflowVersionSummarySchema),
});

export const DeleteDraftRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
});

export const RegenerateChangelogRequestSchema = z.object({
  workflowDefinitionId: z.uuid(),
});

export const ListPendingApprovalsRequestSchema = z.object({
  limit: z.number().int().min(1).max(100).optional(),
  offset: z.number().int().min(0).optional(),
});

export const PendingApprovalItemSchema = z.object({
  workflowRunId: z.string().uuid(),
  workflowId: z.string().uuid(),
  workflowName: z.string(),
  workflowDefinitionId: z.string().uuid(),
  triggeredAt: z.iso.datetime(),
  triggeredByName: z.string().nullable(),
  stepRunId: z.string().uuid(),
  stepName: z.string(),
  instructions: z.string().nullable(),
  reviewData: z.record(z.string(), z.unknown()).nullable(),
});

export const ListPendingApprovalsResponseSchema = z.object({
  approvals: z.array(PendingApprovalItemSchema),
  total: z.number(),
});

export type PendingApprovalItem = z.infer<typeof PendingApprovalItemSchema>;
export type ListPendingApprovalsResponse = z.infer<
  typeof ListPendingApprovalsResponseSchema
>;

// ── Automation Score ─────────────────────────────────────────────────

export const GetAutomationScoreRequestSchema = z.object({
  workflowId: z.uuid(),
  workflowDefinitionId: z.uuid().optional(),
});

export const ApprovalStepBreakdownItemSchema = z.object({
  stepName: z.string(),
  totalRuns: z.number().int(),
  skippedRuns: z.number().int(),
  completedRuns: z.number().int(),
  skipRate: z.number(),
});

export const VersionScoreItemSchema = z.object({
  version: z.number().int(),
  totalRuns: z.number().int(),
  automatedRuns: z.number().int(),
  score: z.number().nullable(),
});

export const WeeklyScoreItemSchema = z.object({
  weekStart: z.string(),
  totalRuns: z.number().int(),
  automatedRuns: z.number().int(),
  score: z.number().nullable(),
});

export const GetAutomationScoreResponseSchema = z.object({
  totalCompletedRuns: z.number().int(),
  automatedRuns: z.number().int(),
  score: z.number().nullable(),
  sampleSufficient: z.boolean(),
  hasApprovalSteps: z.boolean(),
  approvalStepBreakdown: z.array(ApprovalStepBreakdownItemSchema),
  versionHistory: z.array(VersionScoreItemSchema),
  avgApprovalDurationMs: z.number().nullable(),
  last7Days: z.object({
    totalCompletedRuns: z.number().int(),
    automatedRuns: z.number().int(),
    score: z.number().nullable(),
  }),
  weeklyHistory: z.array(WeeklyScoreItemSchema),
});

export type GetAutomationScoreRequest = z.infer<
  typeof GetAutomationScoreRequestSchema
>;
export type GetAutomationScoreResponse = z.infer<
  typeof GetAutomationScoreResponseSchema
>;
export type ApprovalStepBreakdownItem = z.infer<
  typeof ApprovalStepBreakdownItemSchema
>;
export type VersionScoreItem = z.infer<typeof VersionScoreItemSchema>;

/**
 * Workflow contracts
 */
export const workflowContracts = {
  // Create a workflow (also creates its first definition/version in the
  // same transaction and pins it).
  createDefinition: oc
    .input(CreateWorkflowDefinitionRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "POST" }),

  // Update version-level content on a specific definition row.
  // Lineage-level edits go through updateWorkflow.
  updateDefinition: oc
    .input(UpdateWorkflowDefinitionRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "PUT" }),

  // Get a specific version (WorkflowDefinition row) by ID.
  getDefinition: oc
    .input(z.object({ workflowDefinitionId: z.uuid() }))
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "GET" }),

  // Get the Workflow (lineage-level entity) by ID.
  getWorkflow: oc
    .input(z.object({ workflowId: z.uuid() }))
    .output(WorkflowResponseSchema)
    .route({ method: "GET" }),

  // Update workflow-level fields (name, tags, isActive, orchestratorType,
  // etc.) — applies immediately, affects every version.
  updateWorkflow: oc
    .input(UpdateWorkflowRequestSchema)
    .output(WorkflowResponseSchema)
    .route({ method: "PATCH" }),

  // List workflow definitions
  listDefinitions: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(
      z.object({
        workflows: z.array(WorkflowDefinitionResponseSchema),
        total: z.number(),
      }),
    )
    .route({ method: "GET" }),

  // Trigger a workflow
  trigger: oc
    .input(TriggerWorkflowRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({ method: "POST" }),

  // Trigger a scheduled workflow immediately (manual run for testing)
  triggerScheduleNow: oc
    .input(z.object({ workflowDefinitionId: z.uuid() }))
    .output(
      z.object({
        success: z.boolean(),
        message: z.string(),
        workflowRunId: z.uuid().optional(),
      }),
    )
    .route({ method: "POST" }),

  // Run the Observe DataSource Link initial ingest now (manual equivalent of the
  // publish-time initial run). Starts one phase:"initial" run per observed link.
  triggerObserveInitialIngest: oc
    .input(z.object({ workflowDefinitionId: z.uuid() }))
    .output(
      z.object({
        success: z.boolean(),
        message: z.string(),
        workflowRunIds: z.array(z.uuid()),
      }),
    )
    .route({ method: "POST" }),

  // Get workflow run status
  getRun: oc
    .input(GetWorkflowRunRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({ method: "GET" }),

  // Get step runs for a workflow run
  getStepRuns: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(
      z.object({
        stepRuns: z.array(StepRunResponseSchema),
      }),
    )
    .route({ method: "GET" }),

  // Get agent tool-call StepRuns recorded against this workflow run (e.g.
  // computer-use operator iterations from a remote-desktop step). These carry
  // a null workflowStepId and so are excluded from getStepRuns; the trace nests
  // them under their `parentStepRunId` step.
  getWorkflowRunToolCalls: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(
      z.object({
        toolCalls: z.array(ToolCallRunResponseSchema),
      }),
    )
    .route({ method: "GET" }),

  // Get logs for a step run
  getStepRunLogs: oc
    .input(z.object({ stepRunId: z.uuid() }))
    .output(
      z.object({
        logs: z.array(
          z
            .object({
              level: z.number(),
              time: z.string(),
              msg: z.string(),
              workflowRunId: z.string().optional(),
              stepRunId: z.string().optional(),
              stepType: z.string().optional(),
              stepName: z.string().optional(),
            })
            .passthrough(),
        ),
      }),
    )
    .route({ method: "GET" }),

  // Sandbox telemetry for a `code_sandbox` step run — the nested in-sandbox
  // trace + lifetime metrics rollup. Served from our materialized tables (no
  // live Daytona call). `sandbox` is null for non-sandbox steps; `pending` is
  // true until the background ingest has pulled telemetry (`materialized_at`).
  getStepRunSandbox: oc
    .input(z.object({ stepRunId: z.uuid() }))
    .output(
      z.object({
        sandbox: sandboxInstanceSchema.nullable(),
        spans: z.array(sandboxSpanSchema),
      }),
    )
    .route({ method: "GET" }),

  // Sandbox stdout/stderr/system logs for a `code_sandbox` step run.
  getStepRunSandboxLogs: oc
    .input(z.object({ stepRunId: z.uuid() }))
    .output(
      z.object({
        lines: z.array(sandboxLogLineSchema),
        truncated: z.boolean(),
      }),
    )
    .route({ method: "GET" }),

  // Get unified logs for all steps in a workflow run (includes child/fanout runs)
  getWorkflowRunLogs: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(
      z.object({
        sections: z.array(
          z.object({
            stepRunId: z.string(),
            stepName: z.string(),
            stepType: z.string(),
            status: z.string(),
            depth: z.number(),
            childRunLabel: z.string().nullable(),
            logs: z.array(
              z
                .object({
                  level: z.number(),
                  time: z.string(),
                  msg: z.string(),
                })
                .passthrough(),
            ),
          }),
        ),
      }),
    )
    .route({ method: "GET" }),

  // List available step types
  listStepTypes: oc
    .input(z.object({ workflowDefinitionId: z.uuid().optional() }))
    .output(ListStepTypesResponseSchema)
    .route({ method: "GET" }),

  listActions: oc
    .input(z.object({}))
    .output(ListActionWorkflowOptionsResponseSchema)
    .route({ method: "GET" }),

  // List available workflow templates
  listTemplates: oc
    .input(z.object({}))
    .output(ListWorkflowTemplatesResponseSchema)
    .route({ method: "GET" }),

  // Instantiate a workflow template
  instantiateTemplate: oc
    .input(InstantiateWorkflowTemplateRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "POST" }),

  // List workflow definition summaries (with node count and last run)
  listDefinitionSummaries: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
        includeDeleted: z.boolean().optional(),
        // Server-side filters (the workflows list paginates on the backend).
        search: z.string().optional(),
        mine: z.boolean().optional(),
        published: z.boolean().optional(),
        paused: z.boolean().optional(),
        scheduled: z.boolean().optional(),
        event: z.boolean().optional(),
        tags: z.array(z.string()).optional(),
        sortBy: z.enum(["name", "createdAt", "updatedAt"]).optional(),
        sortDirection: z.enum(["asc", "desc"]).optional(),
      }),
    )
    .output(ListWorkflowDefinitionSummariesResponseSchema)
    .route({ method: "GET" }),

  listGroups: oc
    .input(
      z.object({
        limit: z.number().optional(),
        offset: z.number().optional(),
      }),
    )
    .output(
      z.object({
        groups: z.array(WorkflowGroupSummarySchema),
        total: z.number(),
      }),
    )
    .route({ method: "GET" }),

  getGroup: oc
    .input(z.object({ workflowGroupId: z.uuid() }))
    .output(WorkflowGroupResponseSchema)
    .route({ method: "GET" }),

  createGroup: oc
    .input(CreateWorkflowGroupRequestSchema)
    .output(WorkflowGroupResponseSchema)
    .route({ method: "POST" }),

  updateGroup: oc
    .input(UpdateWorkflowGroupRequestSchema)
    .output(WorkflowGroupResponseSchema)
    .route({ method: "PUT" }),

  listGroupsForWorkflow: oc
    .input(z.object({ workflowId: z.uuid() }))
    .output(
      z.object({
        groups: z.array(WorkflowGroupSummarySchema),
      }),
    )
    .route({ method: "GET" }),

  // List runs for a workflow definition
  listRunsForDefinition: oc
    .input(ListWorkflowRunsForDefinitionRequestSchema)
    .output(ListWorkflowRunsForDefinitionResponseSchema)
    .route({ method: "GET" }),

  // List rerun children for a given workflow run
  listRerunsForRun: oc
    .input(ListRerunsForRunRequestSchema)
    .output(ListRerunsForRunResponseSchema)
    .route({ method: "GET" }),

  // Trigger a rerun of an existing workflow run (extracts eventData server-side)
  rerunFromRun: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(WorkflowRunResponseSchema)
    .route({ method: "POST" }),

  // Dev-only: trigger a draft definition using event data extracted from a
  // past run. Allows iterating on a draft without publishing it each time.
  // Blocked in production environments.
  testDraftWithRun: oc
    .input(
      z.object({
        draftDefinitionId: z.uuid(),
        sourceRunId: z.uuid(),
      }),
    )
    .output(WorkflowRunResponseSchema)
    .route({ method: "POST" }),

  // Retry a failed or timed_out step
  retryStep: oc
    .input(RetryStepRequestSchema)
    .output(RetryStepResponseSchema)
    .route({ method: "POST" }),

  // Restart workflow from a specific step (cancels downstream steps)
  restartFromStep: oc
    .input(RestartFromStepRequestSchema)
    .output(RestartFromStepResponseSchema)
    .route({ method: "POST" }),

  // Resolve a guardrail review (confirm violation or dismiss as false positive)
  resolveGuardrailReview: oc
    .input(ResolveGuardrailReviewRequestSchema)
    .output(ResolveGuardrailReviewResponseSchema)
    .route({ method: "POST" }),

  // Get workflow run and executed steps for a specific entity
  getEntityWorkflowContext: oc
    .input(GetEntityWorkflowContextRequestSchema)
    .output(GetEntityWorkflowContextResponseSchema)
    .route({ method: "GET" }),

  // Get child workflow runs for a parent workflow
  getChildWorkflowRuns: oc
    .input(z.object({ parentWorkflowRunId: z.uuid() }))
    .output(
      z.object({
        childWorkflowRuns: z.array(WorkflowRunResponseSchema),
      }),
    )
    .route({ method: "GET" }),

  // Duplicate a workflow definition
  duplicateDefinition: oc
    .input(DuplicateWorkflowDefinitionRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "POST" }),

  // Test a single workflow step in isolation (admin only)
  testStep: oc
    .input(
      z.object({
        stepType: z.string(),
        input: z.record(z.string(), z.unknown()),
        config: z.record(z.string(), z.unknown()),
        timeoutMs: z.number().int().min(1000).max(300_000).optional(),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        output: z.record(z.string(), z.unknown()).optional(),
        emittedEvents: z
          .array(
            z.object({
              eventType: z.string(),
              data: z.unknown(),
            }),
          )
          .optional(),
        error: z.string().optional(),
        durationMs: z.number().optional(),
      }),
    )
    .route({ method: "POST" }),

  // Delete a workflow definition (soft delete)
  deleteDefinition: oc
    .input(z.object({ workflowDefinitionId: z.uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "DELETE" }),

  // Delete a whole workflow (lineage row) — cascades a soft-delete across
  // every version, run, step, edge, and step run under it. Used by the
  // list-page row action, which operates on workflow ids (not versions).
  deleteWorkflow: oc
    .input(z.object({ workflowId: z.uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "DELETE" }),

  // Test case CRUD
  listTestCases: oc
    .input(ListTestCasesRequestSchema)
    .output(z.object({ testCases: z.array(WorkflowTestCaseSchema) }))
    .route({ method: "GET" }),

  createTestCase: oc
    .input(CreateTestCaseRequestSchema)
    .output(WorkflowTestCaseSchema)
    .route({ method: "POST" }),

  updateTestCase: oc
    .input(UpdateTestCaseRequestSchema)
    .output(WorkflowTestCaseSchema)
    .route({ method: "PUT" }),

  deleteTestCase: oc
    .input(DeleteTestCaseRequestSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "DELETE" }),

  runTestCase: oc
    .input(RunTestCaseRequestSchema)
    .output(WorkflowRunResponseSchema)
    .route({ method: "POST" }),

  checkTestCaseCompatibility: oc
    .input(CheckTestCaseCompatibilityRequestSchema)
    .output(CheckTestCaseCompatibilityResponseSchema)
    .route({ method: "POST" }),

  // Cancel an active workflow run
  cancelWorkflowRun: oc
    .input(z.object({ workflowRunId: z.uuid() }))
    .output(WorkflowRunResponseSchema)
    .route({ method: "POST" }),

  // Sync Temporal run statuses for active runs
  syncTemporalRunStatuses: oc
    .input(
      z.object({
        workflowRunIds: z.array(z.uuid()).max(50),
      }),
    )
    .output(
      z.object({
        statuses: z.record(
          z.string(),
          z.object({
            status: WorkflowRunStatusSchema,
            stopReason: z.string().optional(),
          }),
        ),
      }),
    )
    .route({ method: "POST" }),

  // Infrastructure health check
  orchestratorHealth: oc
    .input(z.object({}))
    .output(
      z.object({
        rabbitmq: z.boolean(),
        temporal: z.boolean(),
      }),
    )
    .route({ method: "GET" }),

  // Rerun a set of AP invoices immediately by re-triggering their originating workflow runs
  rerunInvoices: oc
    .input(z.object({ invoiceIds: z.array(z.uuid()).min(1).max(100) }))
    .output(
      z.object({
        results: z.array(
          z.object({
            invoiceId: z.uuid(),
            success: z.boolean(),
            error: z.string().optional(),
          }),
        ),
      }),
    )
    .route({ method: "POST" }),

  // Schedule a future rerun for a set of AP invoices
  snoozeRerunInvoices: oc
    .input(
      z.object({
        invoiceIds: z.array(z.uuid()).min(1).max(100),
        scheduledAt: z.coerce.date(),
      }),
    )
    .output(
      z.object({
        results: z.array(
          z.object({
            invoiceId: z.uuid(),
            success: z.boolean(),
            error: z.string().optional(),
          }),
        ),
      }),
    )
    .route({ method: "POST" }),

  // -------------------------------------------------------------------------
  // Versioning
  // -------------------------------------------------------------------------

  /**
   * Fork a new draft off the lineage's currently-pinned version. If the
   * caller already has an open draft in this lineage, return it instead of
   * creating another one (prevents accidental duplicates per user).
   */
  createDraft: oc
    .input(CreateDraftRequestSchema)
    .output(CreateDraftResponseSchema)
    .route({ method: "POST" }),

  /**
   * Promote a draft to a finalized, immutable version. Assigns version number,
   * triggers async AI changelog generation. If the draft's parent is no longer
   * pinned, returns a stale-base warning; caller must retry with
   * acknowledgeStaleBase=true to proceed.
   */
  finalizeVersion: oc
    .input(FinalizeVersionRequestSchema)
    .output(FinalizeVersionResponseSchema)
    .route({ method: "POST" }),

  /**
   * Mark a finalized version as the canonical version of its lineage.
   * Transactionally unpins the previously-pinned version and re-syncs Temporal
   * schedules.
   */
  pinVersion: oc
    .input(PinVersionRequestSchema)
    .output(PinVersionResponseSchema)
    .route({ method: "POST" }),

  /**
   * Update the changelog text on any version. This is the only field that
   * may be mutated on a finalized version.
   */
  updateVersionChangelog: oc
    .input(UpdateVersionChangelogRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "PATCH" }),

  /** List all versions for a lineage, ordered by version DESC. */
  listVersions: oc
    .input(ListVersionsRequestSchema)
    .output(ListVersionsResponseSchema)
    .route({ method: "GET" }),

  /** List draft versions in the org (optionally filtered to the caller). */
  listDrafts: oc
    .input(ListDraftsRequestSchema)
    .output(ListDraftsResponseSchema)
    .route({ method: "GET" }),

  /** Soft-delete a draft. Only the draft's creator may delete it. Finalized
   *  versions can never be deleted through this endpoint — they're immutable
   *  history (past runs reference them by id). */
  deleteDraft: oc
    .input(DeleteDraftRequestSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "DELETE" }),

  /** Re-run the AI changelog generator on a finalized version. Useful when
   *  the original generation fell back to the mechanical summary (e.g. the
   *  model was down at finalize time). */
  regenerateChangelog: oc
    .input(RegenerateChangelogRequestSchema)
    .output(WorkflowDefinitionResponseSchema)
    .route({ method: "POST" }),

  // Submit a workflow feature request email (fired from chat agent confirmation card)
  submitFeatureRequest: oc
    .input(
      z.object({
        useCase: z.string().trim().min(1).max(2000),
        stepsConsidered: z.array(z.string().trim().min(1).max(200)).max(20),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        message: z.string(),
      }),
    )
    .route({ method: "POST" }),

  // AI-assisted step config modification (POC)
  assistStepConfig: oc
    .input(
      z.object({
        stepType: z.string(),
        currentConfig: z.record(z.string(), z.unknown()),
        configSchema: z.record(z.string(), z.unknown()).optional(),
        stepDescription: z.string().optional(),
        instruction: z.string().min(1).max(2000),
      }),
    )
    .output(
      z.object({
        config: z.record(z.string(), z.unknown()),
        explanation: z.string(),
      }),
    )
    .route({ method: "POST" }),
  submitWorkflowApproval: oc
    .input(
      z.object({
        stepRunId: z.uuid(),
        decision: z.enum(["approved", "rejected", "escalated"]),
        comment: z.string().optional(),
        escalationPath: z
          .string()
          .optional()
          .describe(
            "When decision is 'escalated', the named escalation path socket to route to (from the step's escalationPaths config).",
          ),
      }),
    )
    .output(
      z.object({
        success: z.literal(true),
      }),
    )
    .route({ method: "POST" }),
  listPendingApprovals: oc
    .input(ListPendingApprovalsRequestSchema)
    .output(ListPendingApprovalsResponseSchema)
    .route({ method: "GET" }),
  getAutomationScore: oc
    .input(GetAutomationScoreRequestSchema)
    .output(GetAutomationScoreResponseSchema)
    .route({ method: "GET" }),
} as const;

export const SubmitWorkflowApprovalRequest = z.object({
  stepRunId: z.uuid(),
  decision: z.enum(["approved", "rejected", "escalated"]),
  comment: z.string().optional(),
  escalationPath: z.string().optional(),
});
export type SubmitWorkflowApprovalRequest = z.infer<
  typeof SubmitWorkflowApprovalRequest
>;

/**
 * Export types
 */
export type CreateWorkflowDefinitionRequest = z.infer<
  typeof CreateWorkflowDefinitionRequestSchema
>;
export type UpdateWorkflowDefinitionRequest = z.infer<
  typeof UpdateWorkflowDefinitionRequestSchema
>;
export type WorkflowDefinitionResponse = z.infer<
  typeof WorkflowDefinitionResponseSchema
>;
export type WorkflowResponse = z.infer<typeof WorkflowResponseSchema>;
export type UpdateWorkflowRequest = z.infer<typeof UpdateWorkflowRequestSchema>;
export type TriggerWorkflowRequest = z.infer<
  typeof TriggerWorkflowRequestSchema
>;
export type WorkflowRunResponse = z.infer<typeof WorkflowRunResponseSchema>;
export type GetWorkflowRunRequest = z.infer<typeof GetWorkflowRunRequestSchema>;
export type StepRunResponse = z.infer<typeof StepRunResponseSchema>;
export type ListStepTypesResponse = z.infer<typeof ListStepTypesResponseSchema>;
export type ActionWorkflowInterface = z.infer<
  typeof ActionWorkflowInterfaceSchema
>;
export type ActionWorkflowOption = z.infer<typeof ActionWorkflowOptionSchema>;
export type ListActionWorkflowOptionsResponse = z.infer<
  typeof ListActionWorkflowOptionsResponseSchema
>;
export type WorkflowTemplateResponse = z.infer<
  typeof WorkflowTemplateResponseSchema
>;
export type ListWorkflowTemplatesResponse = z.infer<
  typeof ListWorkflowTemplatesResponseSchema
>;
export type InstantiateWorkflowTemplateRequest = z.infer<
  typeof InstantiateWorkflowTemplateRequestSchema
>;
export type WorkflowDefinitionSummary = z.infer<
  typeof WorkflowDefinitionSummarySchema
>;
export type ListWorkflowDefinitionSummariesResponse = z.infer<
  typeof ListWorkflowDefinitionSummariesResponseSchema
>;
export type WorkflowGroupFrame = z.infer<typeof WorkflowGroupFrameSchema>;
export type WorkflowGroupSummary = z.infer<typeof WorkflowGroupSummarySchema>;
export type WorkflowGroupImplicitLink = z.infer<
  typeof WorkflowGroupImplicitLinkSchema
>;
export type WorkflowGroupResponse = z.infer<typeof WorkflowGroupResponseSchema>;
export type WorkflowGroupMember = z.infer<typeof WorkflowGroupMemberSchema>;
export type CreateWorkflowGroupRequest = z.infer<
  typeof CreateWorkflowGroupRequestSchema
>;
export type UpdateWorkflowGroupRequest = z.infer<
  typeof UpdateWorkflowGroupRequestSchema
>;
export type ListWorkflowRunsForDefinitionResponse = z.infer<
  typeof ListWorkflowRunsForDefinitionResponseSchema
>;
export type ListWorkflowRunsForDefinitionRequest = z.infer<
  typeof ListWorkflowRunsForDefinitionRequestSchema
>;
export type ListRerunsForRunRequest = z.infer<
  typeof ListRerunsForRunRequestSchema
>;
export type RerunSummary = z.infer<typeof RerunSummarySchema>;
export type ListRerunsForRunResponse = z.infer<
  typeof ListRerunsForRunResponseSchema
>;
export type RetryStepRequest = z.infer<typeof RetryStepRequestSchema>;
export type RetryStepResponse = z.infer<typeof RetryStepResponseSchema>;
export type ResolveGuardrailReviewRequest = z.infer<
  typeof ResolveGuardrailReviewRequestSchema
>;
export type ResolveGuardrailReviewResponse = z.infer<
  typeof ResolveGuardrailReviewResponseSchema
>;
export type RestartFromStepRequest = z.infer<
  typeof RestartFromStepRequestSchema
>;
export type RestartFromStepResponse = z.infer<
  typeof RestartFromStepResponseSchema
>;
export type GetEntityWorkflowContextRequest = z.infer<
  typeof GetEntityWorkflowContextRequestSchema
>;
export type EntityWorkflowExecutedStep = z.infer<
  typeof EntityWorkflowExecutedStepSchema
>;
export type GetEntityWorkflowContextResponse = z.infer<
  typeof GetEntityWorkflowContextResponseSchema
>;
export type DuplicateWorkflowDefinitionRequest = z.infer<
  typeof DuplicateWorkflowDefinitionRequestSchema
>;
export type CheckTestCaseCompatibilityResponse = z.infer<
  typeof CheckTestCaseCompatibilityResponseSchema
>;

// Versioning types
export type WorkflowDefinitionStatus = z.infer<
  typeof WorkflowDefinitionStatusSchema
>;
export type WorkflowVersionSummary = z.infer<
  typeof WorkflowVersionSummarySchema
>;
export type CreateDraftRequest = z.infer<typeof CreateDraftRequestSchema>;
export type CreateDraftResponse = z.infer<typeof CreateDraftResponseSchema>;
export type FinalizeVersionRequest = z.infer<
  typeof FinalizeVersionRequestSchema
>;
export type FinalizeVersionResponse = z.infer<
  typeof FinalizeVersionResponseSchema
>;
export type PinVersionRequest = z.infer<typeof PinVersionRequestSchema>;
export type PinVersionResponse = z.infer<typeof PinVersionResponseSchema>;
export type UpdateVersionChangelogRequest = z.infer<
  typeof UpdateVersionChangelogRequestSchema
>;
export type ListVersionsRequest = z.infer<typeof ListVersionsRequestSchema>;
export type ListVersionsResponse = z.infer<typeof ListVersionsResponseSchema>;
export type ListDraftsRequest = z.infer<typeof ListDraftsRequestSchema>;
export type ListDraftsResponse = z.infer<typeof ListDraftsResponseSchema>;
export type DeleteDraftRequest = z.infer<typeof DeleteDraftRequestSchema>;
export type RegenerateChangelogRequest = z.infer<
  typeof RegenerateChangelogRequestSchema
>;
export type StaleBaseWarning = z.infer<typeof StaleBaseWarningSchema>;
